/**
 * **내 작업** — 준비된 작업에서 골라 두거나 직접 만든 한 벌. 사이드바에 뽑아 쓴다.
 *
 * ```
 *   CatchMomentsWorkspace/{id}.menuJobs   작업 목록(이름·무엇을·무엇에·언제·메뉴에 뽑나)
 *     │ workspace.byId (실시간 구독)
 *   AI 작업 화면의 「내 작업」 구역   ← 만들고 고치는 자리
 *     ▼
 *   사이드바 TASK SUPPORT   ← 「메뉴에 고정」한 것만 선다
 * ```
 *
 * 🚨 **라이브러리가 정본이고 사이드바는 «고정한 것»만 비춘다.** Notion 의 커스텀 에이전트가
 *   ••• → 「즐겨찾기에 추가/제거」로 사이드바 표시를 정하고, Linear 의 Views 가 favorite 한
 *   것만 사이드바에 세우는 것과 같은 골격이다. 목록을 두 벌로 두지 않는다.
 * 🔑 **작업 한 벌은 «무엇을 · 무엇에 · 언제»다** — 준비된 작업(`analytics-jobs` 의 `kind`)에
 *   대상과 주기를 붙여 이름을 단 것이 「내 작업」이다. 지시문은 여기 안 적는다(정본은 그 표다).
 * 🔑 **직접 만든 작업은 `extra` 한 줄을 덧붙인다** — 준비된 지시 뒤에 붙는 내 주문이다.
 *   준비된 작업을 그대로 고정하면 `extra` 는 빈 문자열이다.
 * ⚠️ **모르는 작업 열쇠는 통째로 떨군다** — `analytics-jobs` 에서 사라진 작업을 메뉴에 세우면
 *   눌러도 아무것도 안 열린다(`menu-sets` 의 깎는 규칙과 같다).
 * 🚨 저장은 **목록 통째**다 — 한 줄만 보내면 나머지가 지워진다.
 */

import { ANALYTICS_JOB_KINDS, ANALYTICS_JOB_SCHEDULES } from 'src/constants/analytics-jobs';

/** 사이드바가 감당하는 수 — 이보다 많으면 메뉴가 목록이 된다 */
export const MENU_JOB_MAX = 12;

/** 대상 한 벌 — 갈래마다 한 칸씩만 쓴다(`JobTargetPick` 과 같은 모양) */
export type MenuJobTarget = {
  kind: string;
  setId: string;
  brand: string;
  account: string;
  seedingId: string;
  days: number;
};

export type MenuJob = {
  /** `job:<밀리초>` — 못 고친다 */
  id: string;
  /** 메뉴에 적히는 이름 */
  name: string;
  /** 준비된 작업의 열쇠(`analytics-jobs`) */
  kind: string;
  target: MenuJobTarget;
  /** `once` · `daily` · `weekly` */
  schedule: string;
  /** 사이드바에 뽑나 — 라이브러리에는 늘 있다(머리말) */
  pinned: boolean;
  /** 준비된 지시 뒤에 붙는 내 주문 — 준비된 작업 그대로면 빈 문자열 */
  extra: string;
};

const KNOWN_KINDS = new Set<string>(ANALYTICS_JOB_KINDS.map((row) => row.key));
const KNOWN_SCHEDULES = new Set<string>(ANALYTICS_JOB_SCHEDULES.map((row) => row.key));

const DEFAULT_TARGET: MenuJobTarget = {
  kind: 'set', setId: '', brand: '', account: '', seedingId: '', days: 30,
};

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readTarget(value: unknown): MenuJobTarget {
  const row = (value ?? {}) as Record<string, unknown>;
  const days = Number(row.days);
  return {
    kind: text(row.kind) || DEFAULT_TARGET.kind,
    setId: text(row.setId),
    brand: text(row.brand),
    account: text(row.account),
    seedingId: text(row.seedingId),
    days: Number.isFinite(days) && days > 0 ? days : DEFAULT_TARGET.days,
  };
}

/** 문서에 적힌 것을 읽는다 — 모르는 작업은 떨군다(머리말) */
export function parseMenuJobs(value: unknown): MenuJob[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((raw) => {
      const row = (raw ?? {}) as Record<string, unknown>;
      const id = text(row.id);
      const kind = text(row.kind);
      if (!id || !KNOWN_KINDS.has(kind)) return null;
      const schedule = text(row.schedule);
      return {
        id,
        name: text(row.name) || kind,
        kind,
        target: readTarget(row.target),
        schedule: KNOWN_SCHEDULES.has(schedule) ? schedule : 'once',
        pinned: row.pinned === true,
        extra: text(row.extra),
      };
    })
    .filter((row): row is MenuJob => row !== null)
    .slice(0, MENU_JOB_MAX);
}

/** 겹치지 않는 열쇠 하나 */
export function nextMenuJobId(taken: readonly string[], at: number = Date.now()): string {
  const used = new Set(taken);
  let seq = at;
  while (used.has(`job:${seq}`)) seq += 1;
  return `job:${seq}`;
}

export function addMenuJob(rows: readonly MenuJob[], row: Omit<MenuJob, 'id'>): MenuJob[] {
  if (rows.length >= MENU_JOB_MAX) return [...rows];
  return [...rows, { ...row, id: nextMenuJobId(rows.map((one) => one.id)) }];
}

export function editMenuJob(rows: readonly MenuJob[], id: string, patch: Partial<MenuJob>): MenuJob[] {
  return rows.map((row) => (row.id === id ? { ...row, ...patch, id: row.id } : row));
}

export function removeMenuJob(rows: readonly MenuJob[], id: string): MenuJob[] {
  return rows.filter((row) => row.id !== id);
}

/** 사이드바에 세울 것 — 고정한 것만, 적어 둔 차례 그대로 */
export function pinnedMenuJobs(rows: readonly MenuJob[]): MenuJob[] {
  return rows.filter((row) => row.pinned);
}

/**
 * 차례를 한 칸 옮긴다.
 * ⚠️ **고정 여부와 상관없이 목록 전체에서** 옮긴다 — 사이드바 차례는 이 목록의 차례다.
 */
export function moveMenuJob(rows: readonly MenuJob[], id: string, delta: number): MenuJob[] {
  const from = rows.findIndex((row) => row.id === id);
  if (from < 0) return [...rows];
  const to = Math.max(0, Math.min(rows.length - 1, from + delta));
  if (to === from) return [...rows];
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}
